import type { Project, ProjectRecord, RecordKind, Schedule } from "@/engine/types";
import { EXCEL_DATE_FORMAT, formatDate } from "@/lib/date-format";
import { saveBytes } from "@/desktop/file-io";
import {
  dueLabel,
  hasSeverity,
  IMPACT_LABEL,
  RECORD_KIND_LABEL,
  RECORD_KINDS,
  RECORD_SECTIONS,
  recordCode,
  recordStatusLabel,
  SEVERITY_LABEL,
  SOURCE_LABEL,
  supersededBy,
} from "@/store/record-ops";

const SHEET_NAME: Record<RecordKind, string> = {
  issue: "이슈관리대장",
  risk: "위험관리대장",
  decision: "의사결정 기록",
  policy: "정책 목록",
  change: "변경관리대장",
  assumption: "가정 목록",
};

/** Date-only values stay on their calendar day; timestamps (createdAt, closedAt) use the local day. */
function excelDate(value: string): Date {
  if (value.length > 10) {
    const dt = new Date(value);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!);
}

function safeFilename(value: string): string {
  const cleaned = value.replace(/[\\/:*?"<>|]/g, "_").trim();
  return cleaned || "플랜";
}

/** One sheet per kind that has records, in register order (number ascending). */
export async function exportRecordsExcel(project: Project, schedule: Schedule): Promise<void> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Super Gantt";
  workbook.created = new Date();

  const byId = new Map(project.records.map((r) => [r.id, r]));
  const taskName = new Map(project.tasks.map((t) => [t.id, t.name]));
  const xTitle = new Map(project.executionTasks.map((t) => [t.id, t.title]));
  const wbsOf = (id: string) => {
    const wbs = schedule.tasks.get(id)?.wbs;
    return `${wbs ? `${wbs} ` : ""}${taskName.get(id) ?? ""}`.trim();
  };
  const codes = (ids: string[]) =>
    ids
      .map((id) => byId.get(id))
      .filter((r): r is ProjectRecord => Boolean(r))
      .map(recordCode)
      .join(", ");

  for (const kind of RECORD_KINDS) {
    const rows = project.records.filter((r) => r.kind === kind).sort((a, b) => a.seq - b.seq);
    if (!rows.length) continue;
    const sections = RECORD_SECTIONS[kind];
    const severity = hasSeverity(kind);
    const decisionLike = kind === "decision" || kind === "policy";

    const columns: Array<{ header: string; width: number; value: (r: ProjectRecord) => string | Date | null; date?: boolean }> = [
      { header: "번호", width: 9, value: (r) => recordCode(r) },
      { header: "제목", width: 36, value: (r) => r.title },
      { header: "상태", width: 10, value: (r) => recordStatusLabel(kind, r.status) },
      ...(severity ? [{ header: "심각도", width: 8, value: (r: ProjectRecord) => (r.severity ? SEVERITY_LABEL[r.severity] : "") }] : []),
      { header: "출처", width: 10, value: (r) => (r.source ? SOURCE_LABEL[r.source] : "") },
      { header: "담당자", width: 14, value: (r) => r.assignees.join(", ") },
      { header: dueLabel(kind), width: 12, value: (r) => (r.due ? excelDate(r.due) : null), date: true },
      ...(kind !== "policy" ? [{ header: "영향", width: 14, value: (r: ProjectRecord) => r.impacts.map((i) => IMPACT_LABEL[i]).join(", ") }] : []),
      ...sections.map((d) => ({ header: d.label, width: 40, value: (r: ProjectRecord) => r.sections[d.key] ?? "" })),
      ...(decisionLike
        ? [
            { header: "대체한 기록", width: 11, value: (r: ProjectRecord) => (r.supersedes ? codes([r.supersedes]) : "") },
            { header: "대체된 기록", width: 11, value: (r: ProjectRecord) => { const next = supersededBy(project.records, r.id); return next ? recordCode(next) : ""; } },
          ]
        : []),
      { header: "연결 WBS", width: 28, value: (r) => r.linkedTaskIds.map(wbsOf).join("\n") },
      { header: "대응 Task", width: 28, value: (r) => r.linkedExecutionTaskIds.map((id) => xTitle.get(id) ?? "").filter(Boolean).join("\n") },
      { header: "관련 기록", width: 14, value: (r) => codes(r.linkedRecordIds) },
      { header: "첨부", width: 28, value: (r) => r.attachments.map((a) => (a.url.startsWith("data:") ? a.title : `${a.title} ${a.url}`)).join("\n") },
      {
        header: "댓글",
        width: 44,
        value: (r) =>
          r.comments
            .filter((c) => !c.deleted)
            .map((c) => `${formatDate(c.at, "numeric")} ${c.author}${c.parentId ? " (답글)" : ""}: ${c.body}`)
            .join("\n"),
      },
      { header: "등록일", width: 12, value: (r) => excelDate(r.createdAt), date: true },
      { header: "종결일", width: 12, value: (r) => (r.closedAt ? excelDate(r.closedAt) : null), date: true },
    ];

    const sheet = workbook.addWorksheet(SHEET_NAME[kind], { views: [{ state: "frozen", xSplit: 2, ySplit: 1 }] });
    sheet.columns = columns.map((c, i) => ({ key: `c${i}`, width: c.width }));
    sheet.addRow(columns.map((c) => c.header));
    for (const r of rows) sheet.addRow(columns.map((c) => c.value(r)));

    const header = sheet.getRow(1);
    header.height = 22;
    header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAED" } };
    header.font = { color: { argb: "FF424242" } };
    columns.forEach((c, i) => {
      if (c.date) sheet.getColumn(i + 1).numFmt = EXCEL_DATE_FORMAT;
    });
    sheet.eachRow((row, n) => {
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        cell.alignment = { vertical: "top", wrapText: n > 1, ...(columns[col - 1]?.date ? { horizontal: "right" as const } : {}) };
        cell.border = { bottom: { style: "hair", color: { argb: "FFD9D9D9" } }, right: { style: "hair", color: { argb: "FFE5E5E5" } } };
      });
    });
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: sheet.rowCount, column: columns.length } };
  }

  if (!workbook.worksheets.length) workbook.addWorksheet(RECORD_KIND_LABEL.issue).addRow(["등록된 기록이 없습니다"]);

  const buffer = await workbook.xlsx.writeBuffer();
  await saveBytes(`${safeFilename(project.name)}_관리대장.xlsx`, buffer as ArrayBuffer, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
}
